import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTranslations } from '../context/TranslationsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const { i18n } = useTranslation();
  const { td: tCms } = useTranslations(['auth', 'common', 'errors']);
  const lang = i18n.language;
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    return (cmsValue && cmsValue !== key) ? cmsValue : fallback;
  };

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError(t('auth.emailRequired', 'Enter your email'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t('errors.tooManyAttempts', 'Too many attempts. Try again in a few minutes.'));
      } else {
        setError(err.response?.data?.errorPl || err.response?.data?.error || 
          t('errors.general', 'An error occurred. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-4">
            {t('auth.checkInbox', 'Check your inbox')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('auth.resetEmailSentP1', 'If an account with')} {email} {t('auth.resetEmailSentP2', 'exists, we have sent a password reset link.')}
          </p>
          <p className="text-muted-foreground text-sm mb-6">
            {t('auth.linkExpiresInfo', 'The link will expire in 1 hour. Also check your spam folder.')}
          </p>
          <Link to="/login" className="btn-primary inline-flex items-center justify-center gap-2 w-full">
            <ArrowLeft className="w-5 h-5" />
            {t('auth.backToLogin', 'Back to login')}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8 max-w-md w-full"
      >
        <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />
          {t('common.back', 'Back')}
        </Link>

        <h1 className="text-2xl font-bold text-center mb-2">
          {t('auth.forgotPasswordTitle', 'Forgot password')}
        </h1>
        <p className="text-muted-foreground text-center mb-8">
          {t('auth.enterAccountEmail', 'Enter the email address associated with your account')}
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-500 p-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label>Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                placeholder="twoj@email.pl"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !email}
            className="btn-primary w-full disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('auth.sending', 'Sending...')}
              </div>
            ) : (
              t('auth.sendResetLink', 'Send reset link')
            )}
          </button>
        </form>

        <div className="mt-8 text-center text-sm text-muted-foreground">
          {t('auth.rememberPassword', 'Remember your password?')}{' '}
          <Link to="/login" className="text-primary hover:underline font-medium">
            {t('auth.logIn', 'Log in')}
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
